import React, { useCallback, useMemo } from 'react';
import { Team, Tag, User, UserStatus } from '../types/user';
import TeamSelector from './TeamSelector';
import TagSelector from './TagSelector';
import RoleSelector from './RoleSelector';
import { UserRole } from '../../settings/UserRoles';
import { PaperAirplaneIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { Invitation } from '../../../serverActions/admin';
import { useAdmin } from '../../../providers/AdminProvider';

interface BulkActionsProps {
  selectedUsers: User[];
  selectedUsersCount: number;
  teams: Team[];
  tags: Tag[];
  clearSelection: () => void;
  onResendInvite: (invitations: Invitation[]) => Promise<void>;
  onRevokeInvite: (invitations: Invitation[]) => Promise<void>;
}

const BulkActions: React.FC<BulkActionsProps> = ({
  selectedUsers,
  selectedUsersCount,
  teams,
  tags,
  clearSelection,
  onResendInvite,
  onRevokeInvite,
}) => {

  const { pendingUserChanges, handleUserChanges } = useAdmin();

  const handleBulkUpdateTeam = useCallback((teamsForUser:string[]) => {
    for (const user of selectedUsers) {
      const userId = user.id;
      const newTeams = teamsForUser;
      handleUserChanges(userId, { teams: newTeams });
    }
  }, [handleUserChanges, selectedUsers]);

  const handleBulkUpdateTag = useCallback((tagToAdd:Tag) => {
    for (const user of selectedUsers) {
      const userId = user.id;
      handleUserChanges(userId, { tags: [tagToAdd.id] });
    }
  }, [selectedUsers, handleUserChanges]);

  const handleBulkUpdateRole = useCallback((role: UserRole) => {
    for (const user of selectedUsers) {
      const userId = user.id;
      const currentRole = pendingUserChanges[userId]?.role || user.role;
      if (currentRole !== role) {
        handleUserChanges(userId, { role });
      }
    }
  }, [selectedUsers, pendingUserChanges, handleUserChanges])

  const handleResendInvite = useCallback(async () => {
    const invitations = selectedUsers.map(user => ({
      id: user.id,
      organization_id: user.teams[0],
      email_address: user.email,
      status: user.status,
      role: user.role,
    }));
    await onResendInvite(invitations);
  }, [selectedUsers, onResendInvite]);

  const handleRevokeInvite = useCallback(async () => {
    const invitations = selectedUsers.map(user => ({
      id: user.id,
      organization_id: user.teams[0],
      email_address: user.email,
      status: user.status,
      role: user.role,
    }));
    await onRevokeInvite(invitations);
  }, [selectedUsers, onRevokeInvite]);

  const anyInvites = useMemo(() => {
    return selectedUsers.some(user => user.status !== UserStatus.Active);
  }, [selectedUsers]);

  return (
    <div className="bg-gray-50 p-4 rounded-md mb-4 flex flex-col sm:flex-row sm:items-center gap-y-2 justify-between mt-4 shadow">
      <div className="text-xs sm:text-sm text-gray-700 flex items-center gap-x-2">
        <span className="font-medium">{selectedUsersCount}</span> users selected
        <button className="flex items-center text-xs text-gray-500 hover:text-gray-700 border border-gray-300 bg-gray-100 rounded-full px-2" onClick={() => {clearSelection()}}>
          <XMarkIcon className="h-3 w-3 mr-1" />
          Clear selection
        </button>
      </div>
      <div className="flex sm:flex-row flex-col sm:space-x-2 space-y-2 sm:items-center text-sm font-normal text-gray-700">
        <div className='flex flex-col gap-y-1 text-xs sm:text-sm'>
          Perform actions on all selected users:
          {anyInvites && <span className='text-xs text-red-500'>Limited action available for invited users</span>}
        </div>
        <div className='flex flex-row space-x-1 sm:space-x-2'>
          <RoleSelector
            selectedRole={undefined}
            onSelectRole={handleBulkUpdateRole}
            className="sm:ml-2"
            disabled={anyInvites}
          />
          <TeamSelector
            teams={teams}
            selectedTeams={[]}
            onSelectTeam={(add) => { handleBulkUpdateTeam(add) }}
            className="sm:ml-2"
            disabled={anyInvites}
          />
          <TagSelector
            tags={tags}
            selectedTags={[]}
            onSelectTag={handleBulkUpdateTag}
            className="sm:ml-2"
            disabled={anyInvites}
          />
          {anyInvites && <div>
            <button
              className="inline-flex items-center px-1 sm:px-3 py-1 sm:py-2 gap-x-1 rounded-md text-xs sm:text-sm font-medium bg-gray-50 text-gray-800 hover:bg-gray-100 border border-gray-300"
              onClick={() => { handleResendInvite().catch(() => {})} }
            >
              <PaperAirplaneIcon className="h-3 w-3 mr-1" aria-hidden="true" />
              Resend
            </button>
          </div>}
          {anyInvites && <div>
            <button
              className="inline-flex items-center px-1 sm:px-3 py-1 sm:py-2 gap-x-1 rounded-md text-xs sm:text-sm font-medium bg-red-50 text-red-800 hover:bg-red-100 border border-gray-300"
              onClick={() => { handleRevokeInvite().catch(() => {})} }
            >
              <TrashIcon className="h-3 w-3 mr-1" aria-hidden="true" />
              Revoke
            </button>
          </div>}
        </div>
      </div>
    </div>
  );
};

export default BulkActions;
